import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, take } from "rxjs/operators";
import { AngularFirestore, DocumentData, QueryFn } from '@angular/fire/firestore';
import { DataService } from '../data.service';
import { InventoryItemDTO } from 'src/app/models/results/inventory-item-dto';
import { constants } from 'src/environments/constants';
import { AngularFireAuth } from '@angular/fire/auth';
import { environment } from 'src/environments/environment';
import { ResultConfigViewModel } from 'src/app/models/results/result-config-view-model';
import { InsightInventoryItem } from 'src/app/models/data/insight/insight-inventory-item';
import { ILocaleInfo, UtilityService } from '../../utility.service';

@Injectable({
  providedIn: 'root'
})
export class InsightDataService implements DataService {

  constructor(
    private store: AngularFirestore,
    private auth: AngularFireAuth,
    private utilityService: UtilityService) { }

  updateResultConfigItem(item: ResultConfigViewModel): Promise<void> {
    return this.store.collection(constants.FIRESTORE_INVENTORY_COLLECTION).doc(item.id).set({ ShowInResults: item.showInResults }, { merge: true });
  }

  getAdminEmailAddresses(): Observable<string[]> {
    return this.store.collection(constants.FIRESTORE_ADMINS_COLLECTION).valueChanges().pipe(
      take(1),
      map(records => {
        return records.map((record: any) => record.email)
      })
    );
  }

  executeDatabaseQuery(): Observable<InventoryItemDTO[]> {
    const localeInfo: ILocaleInfo = this.utilityService.getLocaleInfo();

    const queryFn: QueryFn<DocumentData> = ref => ref.where('Locality', '==', localeInfo.localeName).where('Deleted', '==', false);

    return this.auth.user.pipe(
      take(1),
      switchMap(async user => {
        if (!user) {
          await this.auth.signInWithEmailAndPassword(environment.firebaseUserEmail, environment.firebase.apiKey);
        }
      }),
      switchMap(() => this.store.collection<InsightInventoryItem>(constants.FIRESTORE_INVENTORY_COLLECTION, queryFn).valueChanges()),
      take(1),
      map((insightItems: InsightInventoryItem[]) => {
        return insightItems.map(insightItem => this.mapDTO(insightItem, localeInfo));
      })
    );
  }

  mapDTO(insightItem: InsightInventoryItem, localeInfo: ILocaleInfo): InventoryItemDTO {
    return Object.assign(new InventoryItemDTO(), {
      id: insightItem.ID,
      hasBacklitKeyboard: insightItem.BacklitKeyboard,
      hasFingerprintReader: insightItem.HasFingerprintReader,
      hasPrivacyScreen: insightItem.HasPrivacyScreen,
      hasStylus: insightItem.HasStylus,
      cpuModel: insightItem.CpuModel,
      diskDriveSize: insightItem.DiskDriveSize,
      diskDriveUnit: insightItem.DiskDriveUnit,
      diskDriveType: insightItem.DiskDriveType,
      imageLargeLocation: insightItem.ImageLarge,
      isEducation: insightItem.IsEducation,
      isParallelsCapable: insightItem.IsParallelsCapable,
      listPrice: InsightInventoryItem.getPrice(insightItem),
      locality: insightItem.Locality,
      manufactureName: insightItem.ManufactureName,
      manufacturePart: insightItem.ManufacturePart,
      materialID: insightItem.MaterialID,
      memorySize: insightItem.MemorySize,
      memoryUnit: insightItem.MemoryUnit,
      name: insightItem.ProductDescShort.split(' - ')[0],
      productDescLong: insightItem.ProductDescLong,
      productDescShort: insightItem.ProductDescShort,
      productLine: insightItem.ProductLine,
      productType: insightItem.ProductType,
      formattedScreenSize: insightItem.ScreenSize,
      screenSize: InsightInventoryItem.getScreenSize(insightItem),
      showInResults: insightItem.ShowInResults,
      vendorURL: this.utilityService.getLocalizedUrl(InsightInventoryItem.buildVendorURL(insightItem, localeInfo))
    });
  }
}
